<!-- 20260507 -- Copilot (Claude Sonnet 4.6) -- Modal for generating and managing read-only task share links -->
<template>
  <b-modal
    id="shareModal"
    title="Share Task"
    size="md"
    hide-footer
    @show="onShow"
    @hide="onHide"
  >
    <!-- Generate form: two rows, all items same height per row -->
    <div class="share-generate-form">
      <!-- Row 1: expiry duration -->
      <div class="share-generate-row">
        <span class="share-field-label" id="share-expires-label">Expires in</span>
        <input
          v-model.number="expiryDays"
          type="number"
          min="1"
          max="90"
          class="share-days-input form-control form-control-sm"
          :disabled="generating"
          aria-labelledby="share-expires-label"
        />
        <span class="share-days-suffix">days</span>
      </div>
      <!-- Row 2: checkbox left, button right -->
      <div class="share-generate-row">
        <label class="share-check-label">
          <input
            v-model="includeSubtasks"
            type="checkbox"
            class="share-check-input"
            :disabled="generating"
          />
          Include subtasks
        </label>
        <button
          class="btn btn-primary btn-sm share-generate-btn ml-auto"
          :disabled="generating"
          @click="generateLink"
        >
          <b-icon v-if="!generating" icon="share" class="mr-1" />
          {{ generating ? 'Generating…' : 'Generate Link' }}
        </button>
      </div>
    </div>

    <!-- Generated link result -->
    <div v-if="generatedUrl" class="share-result">
      <div class="share-result-row">
        <input
          :value="generatedUrl"
          readonly
          class="form-control form-control-sm share-result-url"
          @focus="$event.target.select()"
        />
        <button
          class="btn btn-sm share-copy-btn"
          :class="copied ? 'btn-success' : 'btn-outline-secondary'"
          @click="copyUrl"
        >
          <b-icon :icon="copied ? 'check' : 'clipboard'" />
          {{ copied ? 'Copied!' : 'Copy' }}
        </button>
      </div>
      <p class="share-result-expiry">
        Expires {{ formatDate(generatedExpiry) }}
      </p>
    </div>

    <!-- 20260507 -- Copilot (Claude Sonnet 4.6) -- Renamed label: expired links also shown (status badge indicates state) -->
    <div v-if="taskShares.length > 0" class="share-existing">
      <p class="share-existing-title">Links for this task</p>
      <div v-for="s in taskShares" :key="s.token" class="share-existing-item">
        <div class="share-existing-info">
          <a :href="shareUrl(s.token)" target="_blank" rel="noopener" class="share-existing-token">{{ s.token }}</a>
          <span
            class="badge share-status-badge"
            :class="s.status === 'active' ? 'badge-success' : 'badge-secondary'"
          >{{ s.status }}</span>
          <span class="share-existing-expiry">Expires {{ formatDate(s.expiresAt) }}</span>
        </div>
        <button
          v-if="s.status === 'active'"
          class="btn btn-outline-danger btn-xs share-revoke-btn"
          @click="revokeLink(s.token)"
        >Revoke</button>
      </div>
    </div>

    <!-- Empty state when loaded but nothing active -->
    <p v-else-if="sharesLoaded && !generatedUrl" class="share-empty-state">
      No links for this task yet.
    </p>

  </b-modal>
</template>

<script>
import ShareService from '../services/ShareService';

export default {
  name: 'ShareModal',

  data() {
    return {
      task:          null,
      expiryDays:    5,
      includeSubtasks: true,
      generating:    false,
      generatedUrl:  null,
      generatedExpiry: null,
      copied:        false,
      taskShares:    [],
      sharesLoaded:  false,
    };
  },

  methods: {
    // Called by AppLayout/$root listener to set the task and open the modal
    open(task) {
      this.task            = task;
      this.generatedUrl    = null;
      this.generatedExpiry = null;
      this.copied          = false;
      this.sharesLoaded    = false;
      this.taskShares      = [];
      this.includeSubtasks = !!(task.children && task.children.length > 0);
      this.$bvModal.show('shareModal');
    },

    onShow() {
      this.loadTaskShares();
    },

    onHide() {
      this.generatedUrl  = null;
      this.generatedExpiry = null;
      this.copied        = false;
    },

    async loadTaskShares() {
      if (!this.task?._id) return;
      try {
        const token  = this.$store.getters.getUserToken;
        const res    = await ShareService.getShares(token);
        const taskId = String(this.task._id);
        this.taskShares  = (res.data?.data || []).filter(s => s.taskId === taskId);
        this.sharesLoaded = true;
      } catch (_) {
        this.taskShares  = [];
        this.sharesLoaded = true;
      }
    },

    async generateLink() {
      if (!this.task?._id) return;
      this.generating  = true;
      this.generatedUrl = null;
      this.copied      = false;

      try {
        const token = this.$store.getters.getUserToken;
        const res   = await ShareService.createShare({
          taskId:          String(this.task._id),
          expirationDays:  this.expiryDays,
          includeSubtasks: this.includeSubtasks,
        }, token);

        if (res.data?.successful) {
          const { token, expiresAt } = res.data.data;
          this.generatedUrl    = `${window.location.origin}/share/${token}`;
          this.generatedExpiry = expiresAt;
          await this.loadTaskShares();
        } else {
          this.$bvToast.toast('Could not generate share link.', { title: 'Share', variant: 'danger', solid: true });
        }
      } catch (_) {
        this.$bvToast.toast('Could not generate share link.', { title: 'Share', variant: 'danger', solid: true });
      } finally {
        this.generating = false;
      }
    },

    async copyUrl() {
      if (!this.generatedUrl) return;
      try {
        await navigator.clipboard.writeText(this.generatedUrl);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2500);
      } catch (_) {
        this.$bvToast.toast('Could not copy to clipboard.', { title: 'Copy', variant: 'warning', solid: true });
      }
    },

    async revokeLink(shareToken) {
      try {
        const token = this.$store.getters.getUserToken;
        await ShareService.revokeShare(shareToken, token);
        this.$bvToast.toast('Link revoked.', { title: 'Share', variant: 'success', solid: true });
        if (this.generatedUrl?.includes(shareToken)) {
          this.generatedUrl    = null;
          this.generatedExpiry = null;
        }
        await this.loadTaskShares();
      } catch (_) {
        this.$bvToast.toast('Could not revoke link.', { title: 'Share', variant: 'danger', solid: true });
      }
    },

    shareUrl(token) {
      return `${window.location.origin}/share/${token}`;
    },

    formatDate(epochMs) {
      if (!epochMs) return '—';
      return new Date(epochMs).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
/* ── Generate form ─────────────────────────────────────────────────────────── */
/* Two rows, each a flat flex row with same-height siblings — guarantees a
   true shared midline per row with no nested-column height mismatch. */
.share-generate-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.share-generate-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.share-field-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  white-space: nowrap;
}

.share-days-input { width: 80px; }
.share-days-suffix { font-size: 0.875rem; color: #6c757d; white-space: nowrap; }

.share-check-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.875rem;
  cursor: pointer;
  margin: 0;
  white-space: nowrap;
  user-select: none;
}

.share-check-input {
  cursor: pointer;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  margin: 0;
}

.share-generate-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Result ───────────────────────────────────────────────────────────────── */
.share-result {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.share-result-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.share-result-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  flex: 1;
}

.share-copy-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.share-result-expiry {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0.4rem 0 0;
}

/* ── Existing links ───────────────────────────────────────────────────────── */
.share-existing { border-top: 1px solid #e9ecef; padding-top: 0.75rem; }

.share-existing-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  margin: 0 0 0.5rem;
}

.share-existing-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3rem 0;
  border-bottom: 1px solid #f8f8f8;
  gap: 0.5rem;
}

.share-existing-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-width: 0;
}

.share-existing-token {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  color: #0d6efd;
  text-decoration: none;
}

.share-existing-token:hover { text-decoration: underline; }

.share-status-badge { font-size: 0.65rem; }

.share-existing-expiry {
  font-size: 0.75rem;
  color: #6c757d;
}

.share-revoke-btn {
  padding: 1px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.share-empty-state {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0;
}

.btn-xs {
  padding: 1px 6px;
  font-size: 0.75rem;
  border-radius: 3px;
}
</style>
